import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useMemo,
  useCallback,
} from 'react';
import { SessionService, Message } from '../services/SessionService';
import { useAuth } from './AuthContext';
import logger from '../logging/logger';

interface InitialSessionState {
  messages: Message[];
  isResponding?: boolean;
  isWaitingForResponse?: boolean;
  currentStreamingMessageId?: string | null;
}

interface LocalSessionContextType {
  sessionId: string | null;
  messages: Message[];
  isResponding: boolean;
  isWaitingForResponse: boolean;
  isLoadingMessages: boolean;
  sessionExists: boolean | null;
  currentStreamingMessageId: string | null;

  // Pagination state
  hasMoreMessages: boolean;
  isLoadingMoreMessages: boolean;
  currentPage: number;

  setSessionId: (sessionId: string | null) => void;

  addMessage: (message: Message) => void;
  updateMessage: (messageId: string, updates: Partial<Message>) => void;
  upsertMessage: (message: Message) => void; // Atomically add or update by id

  removeMessage: (messageId: string) => void;
  clearMessages: () => void;

  setWaitingForResponse: (waiting: boolean) => void;
  startResponding: (messageId: string) => void;
  stopResponding: () => void;

  appendToStreamingMessage: (messageId: string, content: string) => void;
  completeStreamingMessage: (messageId: string) => void;

  loadSession: (sessionId: string) => Promise<boolean>;
  loadMoreMessages: (sessionId: string) => Promise<boolean>;

  // Initialize session with state passed from NewChatPage during navigation
  initializeFromHandoff: (state: InitialSessionState) => void;
}

const LocalSessionContext = createContext<LocalSessionContextType | undefined>(undefined);

interface LocalSessionProviderProps {
  children: ReactNode;
}

export const LocalSessionProvider: React.FC<LocalSessionProviderProps> = ({ children }) => {
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [isResponding, setIsResponding] = useState(false);
  const [isWaitingForResponse, setIsWaitingForResponse] = useState(false);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [sessionExists, setSessionExists] = useState<boolean | null>(null);
  const [currentStreamingMessageId, setCurrentStreamingMessageId] = useState<string | null>(null);
  
  // Pagination state
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [isLoadingMoreMessages, setIsLoadingMoreMessages] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const { userUuid, getToken } = useAuth();
  const sessionService = useMemo(() => new SessionService(), []);

  const setWaitingForResponse = useCallback((waiting: boolean) => {
    setIsWaitingForResponse(waiting);
  }, []);

  const loadSession = useCallback(
    async (sid: string): Promise<boolean> => {
      if (!userUuid) return false;

      try {
        setIsLoadingMessages(true);
        setSessionExists(null);
        setCurrentPage(1); // Reset pagination
        setHasMoreMessages(false);

        const token = await getToken();
        if (!token) return false;

        const sessionData = await sessionService.getSession(sid, userUuid, token);
        if (!sessionData) {
          setSessionExists(false);
          setMessages([]);
          return false;
        }

        setSessionExists(true);
        const { messages: messagesData, hasMore } = await sessionService.getMessages(sid, userUuid, token, 1, 50);

        // Debug logging for messages with backtest_result
        const backtestMessages = messagesData.filter((m: any) => m.contentObj?.backtest_result);
        if (backtestMessages.length > 0) {
          logger.info('🔍 LocalSessionContext: Found messages with backtest_result:', backtestMessages);
        }
        logger.info('📦 LocalSessionContext: Loaded messages:', messagesData.length, 'total, backtest_result count:', backtestMessages.length);

        setMessages(messagesData);
        setHasMoreMessages(hasMore);

        return true;
      } catch (error) {
        logger.error('Failed to load session:', error);
        setSessionExists(false);
        setMessages([]);
        setHasMoreMessages(false);
        return false;
      } finally {
        setIsLoadingMessages(false);
      }
    },
    [userUuid, getToken, sessionService]
  );

  const loadMoreMessages = useCallback(
    async (sid: string): Promise<boolean> => {
      if (!userUuid || isLoadingMoreMessages || !hasMoreMessages) return false;

      try {
        setIsLoadingMoreMessages(true);

        const token = await getToken();
        if (!token) return false;

        const nextPage = currentPage + 1;
        const { messages: olderMessages, hasMore } = await sessionService.getMessages(sid, userUuid, token, nextPage, 50);
        
        // Prepend older messages to the beginning of the array
        setMessages(prev => [...olderMessages, ...prev]);
        setHasMoreMessages(hasMore);
        setCurrentPage(nextPage);

        return true;
      } catch (error) {
        logger.error('Failed to load more messages:', error);
        return false;
      } finally {
        setIsLoadingMoreMessages(false);
      }
    },
    [userUuid, getToken, sessionService, isLoadingMoreMessages, hasMoreMessages, currentPage]
  );

  const addMessage = useCallback((message: Message) => {
    setMessages((prev) => [...prev, message]);
  }, []);

  const updateMessage = useCallback((messageId: string, updates: Partial<Message>) => {
    setMessages((prev) =>
      prev.map((msg) => {
        if (msg.id !== messageId) return msg;

        // Merge contentObj (avoid wiping charts when updating text, etc.)
        const nextContentObj =
          updates.contentObj && typeof updates.contentObj === 'object'
            ? { ...(msg.contentObj ?? {}), ...(updates.contentObj ?? {}) }
            : updates.contentObj ?? msg.contentObj;

        return {
          ...msg,
          ...updates,
          contentObj: nextContentObj,
        };
      })
    );
  }, []);

  // Atomically add or update a message by id (avoids stale closure issues)
  const upsertMessage = useCallback((message: Message) => {
    setMessages((prev) => {
      const existingIndex = prev.findIndex((msg) => msg.id === message.id);
      if (existingIndex >= 0) {
        // Update existing
        const updated = [...prev];
        updated[existingIndex] = message;
        return updated;
      } else {
        // Add new
        return [...prev, message];
      }
    });
  }, []);

  const removeMessage = useCallback((messageId: string) => {
    setMessages((prev) => prev.filter((msg) => msg.id !== messageId));
  }, []);

  const clearMessages = useCallback(() => {
    setMessages([]);
  }, []);

  const startResponding = useCallback((messageId: string) => {
    setIsWaitingForResponse(false); // AI started responding, no longer waiting
    setIsResponding(true);
    setCurrentStreamingMessageId(messageId);
  }, []);

  const stopResponding = useCallback(() => {
    setIsResponding(false);
    setIsWaitingForResponse(false);
    setCurrentStreamingMessageId(null);
  }, []);

  const appendToStreamingMessage = useCallback((messageId: string, content: string) => {
    if (!content) return;
    setMessages((prev) =>
      prev.map((msg) =>
        msg.id === messageId ? { ...msg, content: (msg.content ?? '') + content } : msg
      )
    );
  }, []);

  const completeStreamingMessage = useCallback((_messageId: string) => {
    setCurrentStreamingMessageId(null);
    setIsResponding(false);
  }, []);

  // Initialize session with state passed from NewChatPage during navigation handoff
  // This allows seamless transition without re-fetching messages from API
  const initializeFromHandoff = useCallback((state: InitialSessionState) => {
    logger.info('🔄 [LocalSession] Initializing from handoff with', state.messages.length, 'messages',
      'isResponding:', state.isResponding, 'isWaitingForResponse:', state.isWaitingForResponse);
    setMessages(state.messages);
    setSessionExists(true);
    setIsLoadingMessages(false);
    setCurrentPage(1); // Reset pagination for handoff
    setHasMoreMessages(false); // Assume no more messages for new sessions

    // Set waiting state if AI hasn't started responding yet
    if (state.isWaitingForResponse) {
      setIsWaitingForResponse(true);
    }

    // Set responding state if AI has started streaming
    if (state.isResponding && state.currentStreamingMessageId) {
      setIsResponding(true);
      setCurrentStreamingMessageId(state.currentStreamingMessageId);
    }
  }, []);

  const contextValue = useMemo<LocalSessionContextType>(
    () => ({
      sessionId,
      messages,
      isResponding,
      isWaitingForResponse,
      isLoadingMessages,
      sessionExists,
      currentStreamingMessageId,
      hasMoreMessages,
      isLoadingMoreMessages,
      currentPage,
      setSessionId,
      addMessage,
      updateMessage,
      upsertMessage,
      removeMessage,
      clearMessages,
      setWaitingForResponse,
      startResponding,
      stopResponding,
      appendToStreamingMessage,
      completeStreamingMessage,
      loadSession,
      loadMoreMessages,
      initializeFromHandoff,
    }),
    [
      sessionId,
      messages,
      isResponding,
      isWaitingForResponse,
      isLoadingMessages,
      sessionExists,
      currentStreamingMessageId,
      hasMoreMessages,
      isLoadingMoreMessages,
      currentPage,
      addMessage,
      updateMessage,
      upsertMessage,
      removeMessage,
      clearMessages,
      setWaitingForResponse,
      startResponding,
      stopResponding,
      appendToStreamingMessage,
      completeStreamingMessage,
      loadSession,
      loadMoreMessages,
      initializeFromHandoff,
    ]
  );

  return <LocalSessionContext.Provider value={contextValue}>{children}</LocalSessionContext.Provider>;
};

export const useLocalSession = (): LocalSessionContextType => {
  const context = useContext(LocalSessionContext);
  if (context === undefined) {
    throw new Error('useLocalSession must be used within a LocalSessionProvider');
  }
  return context;
};
